








































import { Action, State, Getter } from "vuex-class";
import ContentMixin from "@/mixins/ContentMixin";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
  IContentSection,
  IPassage,
  IQuestion,
} from "@/store/modules/content/types";
import LoadingMixin from "@/mixins/LoadingMixin";
import { mixins } from "vue-class-component";
import { IAnswer, Answers } from "@/store/types";
import TestTake from "@/components/test/TestTake.vue";
import { Route, NavigationGuardNext } from "vue-router";
import { EventBus } from "@/EventBus";

const namespace: string = "practiceModule";

Component.registerHooks([
  "beforeRouteLeave",
  "beforeRouteEnter",
  "beforeRouteUpdate",
]);

@Component({
  name: "practice-assessment",
  components: {
    TestTake,
  },
})
export default class PracticeAssessment extends mixins(
  LoadingMixin,
  ContentMixin
) {
  @Prop({ default: null })
  public subject!: string;

  @Action("finishSection", { namespace: "flowModule" })
  public finishFlowSection!: (subject: string) => Promise<void>;

  @Action("finishSection", { namespace })
  public finishSection!: any;

  @Action("takeSection", { namespace })
  public takeSection!: any;

  @Action("fetchContentQuestions", { namespace })
  public fetchContentQuestions!: any;

  @Action("fetchContentPassages", { namespace })
  public fetchContentPassages!: any;

  @Action("saveAnswers", { namespace })
  public saveAnswers!: any;

  @Action("saveAnswer", { namespace })
  public saveAnswer!: any;

  @State("section", { namespace })
  public section!: IContentSection;

  @State("questions", { namespace })
  public questions!: IQuestion[];

  @State("passages", { namespace })
  public passages!: IPassage[];

  @Getter("isOngoingSection", { namespace })
  public isOngoingSection!: boolean;

  @Getter("completedSections", { namespace })
  public completedSections!: {};

  @Getter("isSectionsFinished", { namespace })
  public isSectionsFinished!: boolean;

  @Getter("nextSection", { namespace })
  public nextSection!: boolean;

  @Getter("isOnline")
  public isConnected!: boolean;

  private isOffline = false;
  private isOfflineEntryStudent = false;
  private questionNum = 0;
  private prevQuestionNum = 0;
  private isSectionStarted = false;
  private isSectionPaused = false;
  private isSectionTimeout = false;
  private popupVisible = false;
  private popupTitle = "";
  private popupText = "";
  private popupButtons:
    | null
    | {
        type: string;
        label: string;
        event: string;
      }[] = null;
  private onPopupClose: any = () => ({});
  private onPopupConfirm: any = () => ({});

  get startButton(): string {
    return this.isOngoingSection ? "Continue Your Exam" : "Start Your Exam";
  }

  get question(): IQuestion | null {
    const question =
      this.questions && this.questions.find(q => q.num === this.questionNum);
    return question || null;
  }

  get prevQuestion(): IQuestion | null {
    const question =
      this.questions &&
      this.questions.find(q => q.num === this.prevQuestionNum);
    return question || null;
  }

  @Watch("questionNum", { immediate: true })
  public onQuestionNum(n: number, o: number) {
    EventBus.$emit("question:changed");
  }

  private popupReset(): void {
    this.popupVisible = false;
    this.popupText = "";
    this.popupTitle = "";
    this.popupButtons = null;
  }

  private async load(subject: string) {
    this.$Progress.start();
    try {
      this.isLoading = true;

      await this.takeSection(subject);

      await Promise.all([
        this.fetchContentQuestions(this.section.id),
        this.fetchContentPassages(this.section.id),
      ]);

      //this.setAnswerAndTimeSpent(this.section.answers);

      this.$Progress.finish();
    } catch (e) {
      this.isLoading = false;
      this.$Progress.fail();
      throw e;
    } finally {
      this.isLoading = false;
    }
  }

  public updated() {
    if (
      this.section &&
      this.section.hasOwnProperty("subject") &&
      this.section.subject === "Math"
    ) {
      // @ts-ignore
      window.MathJax.typeset();
    }
  }

  public async beforeMount() {
    window.addEventListener("beforeunload", this.notifyAppUnload);

    this.$once("hook:beforeDestroy", () => {
      window.removeEventListener("beforeunload", this.notifyAppUnload);
    });
  }

  public beforeUpdate() {
    if (!this.isSectionStarted) {
      const { answers } = this.section || {};
      const lastQuestionAnswerNum =
        answers && +Object.keys(answers).reverse()[0];

      if (this.section) {
        this.questionNum = lastQuestionAnswerNum || 1;
        this.isSectionStarted = true;
      }
    }
  }

  public destroyed() {
    EventBus.$off("tool:choice");
  }

  public async beforeRouteEnter(
    to: Route,
    from: Route,
    next: NavigationGuardNext<PracticeAssessment>
  ) {
    next(async vm => {
      await vm.load(to.params.subject);
    });
  }

  public async beforeRouteUpdate(
    to: Route,
    from: Route,
    next: NavigationGuardNext<PracticeAssessment>
  ) {
    await this.load(to.params.subject);
    next();
  }

  public async beforeRouteLeave(
    to: Route,
    from: Route,
    next: NavigationGuardNext<PracticeAssessment>
  ) {
    if (
      !this.isSectionsFinished &&
      this.isSectionStarted &&
      !this.isSectionTimeout
    ) {
      this.popupText = "The section is not completed.";
      this.popupTitle = "Unfinished section";
      this.popupButtons = [
        {
          type: "danger",
          label: "Leave the test",
          event: "section:leave:click",
        },
      ];
      this.popupVisible = true;

      this.onPopupClose = () => {
        this.popupReset();
      };

      this.onPopupConfirm = async () => {
        this.popupReset();
        await next();
      };
    } else {
      await next();
    }
  }

  private async saveSectionProgress() {
    const { id, num } = this.question || {};
    const spentTime = (q: any) => q && (q.time_spent || 0) + 0;
    const studentAnswer = (q: any) => q && q.student_answer;

    if (this.section) {
      const answer = {
        usection_id: this.section.id,
        question_id: this.prevQuestion ? this.prevQuestion.id : id,
        question_num: this.prevQuestion ? this.prevQuestion.num : num,
        time_spent: spentTime(this.prevQuestion || this.question),
        time_remain: 0,
        answer: studentAnswer(this.prevQuestion || this.question),
      };

      if (answer.answer) {
        await this.saveAnswer(answer);
      }
    }
  }

  private setAnswerAndTimeSpent(answers: Answers) {
    for (const key in answers) {
      const question = this.questions.find(
        q => q.id === answers[key].question_id
      );

      if (question) {
        if (answers[key].answer) {
          this.$set(question, "student_answer", answers[key].answer);
        } else {
          this.$set(question, "student_answer", answers[key].answer_obj);
        }

        this.$set(question, "time_spent", answers[key].time_spent);
      }
    }
  }

  private async notifyAppUnload(event: any) {
    const shouldWarnningBeforeUnload =
      (this.isSectionStarted && !this.isSectionTimeout) ||
      (this.isOffline && this.isOfflineEntryStudent);

    const shouldSaveSectionProgress =
      this.isSectionStarted && !this.isSectionTimeout && !this.isOffline;

    if (shouldWarnningBeforeUnload) {
      event.preventDefault();
      event.returnValue = "";
    }

    if (shouldSaveSectionProgress) {
      await this.saveSectionProgress();
    }
  }

  private async handleSaveAnswer(ansawer: IAnswer): Promise<void> {
    //donothing
  }

  private async handleSectionFinish() {
    let ics: string = "s";

    //counters
    let i: number = 0;
    let c: number = 0;
    let s: number = 0;

    for (const prop in this.questions) {
      if (!this.questions[prop].student_answer) {
        this.$set(this.questions[prop], "student_answer", "SKIP");
      }
      //questions
      this.$set(this.questions[prop], "question_id", this.questions[prop].id); // just set 'question_id'
      this.$set(this.questions[prop], "ics", ics); // result

      if (
        this.questions[prop].student_answer === this.questions[prop].correct
      ) {
        ics = "c";
        c++;
      } else if (this.questions[prop].student_answer === "SKIP") {
        ics = "s";
        s++;
      } else {
        ics = "i";
        i++;
      }

      this.$set(this.questions[prop], "ics", ics); // result
    }

    //section
    const completedSections: any = this.completedSections;
    completedSections[this.subject]["questions"] = this.questions;

    completedSections[this.subject]["i"] = i;
    completedSections[this.subject]["c"] = c;
    completedSections[this.subject]["s"] = s;
    completedSections[this.subject]["raw"] = c;
    completedSections[this.subject]["score"] = c;

    try {
      this.$Progress.start();
      await this.finishSection({
        subject: this.subject,
        usectionId: this.section.id,
        timeRemain: 0,
        answers: this.questions,
        completedSections: completedSections,
      });

      this.$Progress.finish();
    } catch (e) {
      this.$Progress.fail();
      throw e;
    }
  }

  private onChangeCurrentQuestion(num: number) {
    this.prevQuestionNum = this.questionNum;
    this.questionNum = num;
  }

  private async onSectionStarted() {
    this.isSectionStarted = true;
    if (this.isOngoingSection) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      this.$nextTick(async () => {});
    }
  }

  private async onSectionFinished(): Promise<void> {
    await this.handleSectionFinish();
    await this.gotoNext();
  }

  private async gotoNext() {
    if (this.nextSection) {
      this.isSectionStarted = false;
      await this.$router.push("/practice/wait");
    } else {
      await this.$router.push("/practice/done");
    }
  }
}
